import {
  type AudioVideoFacade,
  type AudioVideoObserver,
  type MeetingSessionStatus,
  MeetingSessionStatusCode,
} from "amazon-chime-sdk-js";

export class AudioVideoStartTimeout extends Error {
  constructor(readonly timeoutMs: number) {
    super(`audioVideoDidStart did not fire within ${timeoutMs}ms`);
    this.name = "AudioVideoStartTimeout";
  }
}

export class AudioVideoStartFailed extends Error {
  // statusCode is the MeetingSessionStatusCode enum NAME (e.g. "Left",
  // "AudioJoinedFromAnotherDevice"), not the numeric code. Mirrors the
  // enrichment pattern used by useChimeObserver Effect 6 for
  // meetingStartFailed/meetingFailed events so admin views render the
  // human-readable name without a lookup.
  constructor(readonly statusCode: string) {
    super(
      `audioVideoDidStop fired with terminal status ${statusCode} before audioVideoDidStart`,
    );
    this.name = "AudioVideoStartFailed";
  }
}

export type WaitHandle = {
  // Resolves when audioVideoDidStart fires; rejects on terminal stop or
  // timeout; never throws synchronously. Safe to ignore if cancel() is
  // called first — the observer is removed and no further callbacks fire.
  promise: Promise<void>;
  // Removes the observer + clears the timeout. Idempotent.
  // Resolves the pending promise (rather than rejecting) — cancellation is
  // caller-initiated and the caller has already handled the originating
  // error from meetingManager.start(); a rejection here would surface a
  // second, redundant error. Call from the caller's catch handler if
  // meetingManager.start() throws, to avoid leaking the observer until the
  // 15s timer fires.
  cancel: () => void;
};

// Waits for the SDK's AudioVideoObserver.audioVideoDidStart callback, the
// SDK's documented signal that the WebRTC handshake completed and the
// session is genuinely running.
//
// Default 30s timeout — raised from 15s after the 2026-05-08 join-failure
// cluster (MIN-596) surfaced production cases sitting at 99% of the prior
// 15s budget: Matias-Jofre's iceGatheringDurationMs=14886, Kate Abbott's
// 14615 — both meetingStartFailed payloads ending in
// `canceling FinishGatheringICECandidatesTask`. Doubling the budget
// catches the marginal-but-recoverable population without changing the
// terminal-fail path (audioVideoDidStop fires immediately on terminal
// status, regardless of the timeout). Sustained-failure cases (e.g. Bill
// Gaskins' 8 attempts, none completing) are not helped by any reasonable
// ceiling — they need a browser switch.
//
// Original 15s rationale: ~7.5x the slowest observed handshake (2s on
// Safari 26 / Chrome 147 / macOS Sequoia/Tahoe), with margin for one
// in-handshake reconnect. The cluster proved the slowest-handshake tail
// is wider than the original baseline measured.
//
// Caller MUST register the observer (i.e. invoke this function) BEFORE
// calling meetingManager.start() — cached DTLS sessions or future SDK
// optimizations could fire audioVideoDidStart synchronously inside
// audioVideo.start(). If that happens with after-registration the event
// is missed and the wait hangs for the full 30s. See
// docs/superpowers/plans/2026-05-04-video-webrtc-handshake-race-fix.md.
export function waitForAudioVideoDidStart(
  audioVideo: AudioVideoFacade,
  timeoutMs = 30_000,
): WaitHandle {
  let settled = false;
  let resolve!: () => void;
  let reject!: (err: Error) => void;
  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  const settleAndCleanup = (fn: () => void): void => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    audioVideo.removeObserver(observer);
    fn();
  };

  const observer: AudioVideoObserver = {
    audioVideoDidStart: () => settleAndCleanup(() => resolve()),
    audioVideoDidStop: (status: MeetingSessionStatus) => {
      if (status.isTerminal()) {
        const codeName =
          MeetingSessionStatusCode[status.statusCode()] ??
          `Unknown_${status.statusCode()}`;
        settleAndCleanup(() => reject(new AudioVideoStartFailed(codeName)));
      }
    },
  };

  const timer = setTimeout(
    () => settleAndCleanup(() => reject(new AudioVideoStartTimeout(timeoutMs))),
    timeoutMs,
  );

  audioVideo.addObserver(observer);

  return {
    promise,
    cancel: () => settleAndCleanup(() => resolve()),
  };
}
